import {
  ArticleCardProps,
  ArticleType,
  ButtonProps,
} from '@stadionhq-v3/base-ui';
import { ArticlePage } from '@graphql/__generated__/graphql';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { Category } from '@stadionhq-v3/base-ui/lib/components/organisms/article-card';
import { Locale } from '@core/constants/locales.constants';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import templateConfig from '@templateConfig';
import cloneDeep from 'lodash/cloneDeep';
import { ARTICLE_FORMATS } from '@core/types/pages/articlePage.types';
import { Translations } from '@lib/dictionary-keys/__generated__';
import Image from 'next/image';
import { Lock } from '@components/icons';
import { assetTransform } from '../atoms/asset.transform';

export type ArticleCardSettings = {
  publishDateFormat?: string;
  dictionary: {
    featured?: Translations['news']['label']['featured'];
    membersOnly?: Translations['articleCard']['label']['membersOnly'];
  };
  isSubscribed?: boolean;
  registerCta?: ButtonProps;
  signInCta?: ButtonProps;
};

const articleCardTransform = (
  {
    isFeatured,
    articleTitle,
    publishDate,
    category,
    image,
    author,
    sponsor,
    featuredVideo,
    featuredGallery,
    slug,
    format,
    isRestrictedToMembersOnly,
  }: CleanContentful<ArticlePage>,
  locale: Locale,
  { publishDateFormat, dictionary }: ArticleCardSettings
): ArticleCardProps => {
  const clonedCategory = cloneDeep(category);
  if (clonedCategory && clonedCategory?.category && clonedCategory?.slug) {
    clonedCategory.slug = generateFullPathname({
      locale,
      route: templateConfig.navigation.routes.NEWS_CATEGORY,
      params: { category: clonedCategory.slug },
    });
  }

  return {
    isFeatured,
    featuredLabel: dictionary?.featured,
    type: format as ArticleType,
    slug: generateFullPathname({
      locale,
      route: templateConfig.navigation.routes.ARTICLE,
      params: {
        article: slug,
      },
    }),
    title: articleTitle,
    publishDate: publishDate
      ? {
          date: publishDate,
          format: publishDateFormat || "DD MMM 'YY",
        }
      : undefined,
    category:
      clonedCategory && clonedCategory?.category && clonedCategory?.slug
        ? (clonedCategory as Category)
        : undefined,
    image: image ? (
      <ContentfulImage {...image} />
    ) : (
      <Image
        alt="Thumbnail"
        layout="fill"
        objectFit="cover"
        src={
          featuredVideo?.video?.thumbnailImageUrl ||
          '/images/player-profile-card-fallback-bg.png'
        }
      />
    ),
    author: author?.firstName
      ? {
          avatar: author?.avatar && <ContentfulImage {...author?.avatar} />,
          firstName: author?.firstName,
          secondName: author?.secondName,
          subTitle: author?.subTitle,
        }
      : undefined,
    sponsor: sponsor
      ? { image: <ContentfulImage {...assetTransform(sponsor)} /> }
      : undefined,
    onDark:
      format === ARTICLE_FORMATS.VIDEO || format === ARTICLE_FORMATS.GALLERY,
    duration: { text: featuredVideo?.video?.duration },
    imageCount: featuredGallery?.images?.length,
    membersBadge:
      isRestrictedToMembersOnly && dictionary.membersOnly
        ? {
            leftElement: <Lock />,
            text: dictionary.membersOnly,
          }
        : undefined,
  };
};

export default articleCardTransform;
